import { Quiz } from '@/core/domain/quiz/Quiz.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { LearningSupport } from '@/core/domain/learning/support/LearningSupport'
import {
  type LearningGrainEntity,
  type LearningGrainInputEntity,
  LearningGrainSchema
} from '@/core/infrastructure/schemas/learning/grain/LearningGrainSchema'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'

export class LearningGrain {
  id: number
  title: string
  status: LearningStatus.Values
  points: number
  order: number
  quizPre: Quiz | null = null
  quizPost: Quiz | null = null
  learningSupports: LearningSupport[]

  constructor(grain: LearningGrainEntity) {
    this.id = grain.id
    this.title = grain.title
    this.status = grain.status
    this.points = grain.points
    this.order = grain.order

    // Conversion des quiz s'ils existent
    if (grain.quizPre) {
      this.quizPre = Quiz.create(grain.quizPre)
    } else {
      this.quizPre = null
    }

    if (grain.quizPost) {
      this.quizPost = Quiz.create(grain.quizPost)
    } else {
      this.quizPost = null
    }

    // Conversion des supports, dans leur ordre d'affichage (l'API ne garantit pas l'ordre de la liste)
    this.learningSupports = grain.learningSupports
      .map((support) => LearningSupport.create(support))
      .sort((a, b) => a.order - b.order || a.id - b.id)
  }

  // Retourne le nombre de supports du grain
  get supportsCount(): number {
    return this.learningSupports.length
  }

  // Vérifie si les quiz sont valides (ont une version active avec des questions)
  get quizzesAreValid(): boolean {
    const preQuizValid = this.quizPre ? this.isQuizValid(this.quizPre) : true
    const postQuizValid = this.quizPost ? this.isQuizValid(this.quizPost) : true
    return preQuizValid && postQuizValid
  }

  /**
   * Vérifie si un quiz est valide (a une version active avec des questions)
   * @param quiz - Le quiz à vérifier
   * @returns true si le quiz a une version active avec des questions
   */
  private isQuizValid(quiz: Quiz): boolean {
    const activeVersion = quiz.activeVersion
    return activeVersion !== null && activeVersion.questions.length > 0
  }

  /**
   * Retourne le nombre de questions du quiz pré
   */
  get quizPreQuestionCount(): number {
    if (!this.quizPre) return 0
    const activeVersion = this.quizPre.activeVersion ?? this.quizPre.latestVersion
    return activeVersion ? activeVersion.questions.length : 0
  }

  /**
   * Retourne le nombre de questions du quiz post
   */
  get quizPostQuestionCount(): number {
    if (!this.quizPost) return 0
    const activeVersion = this.quizPost.activeVersion ?? this.quizPost.latestVersion
    return activeVersion ? activeVersion.questions.length : 0
  }

  /**
   * Vérifie si les quiz ont suffisamment de questions pour être publiés
   * @param minimumQuestions - Nombre minimum de questions requis (par défaut 5)
   */
  hasEnoughQuestions(minimumQuestions: number = 5): boolean {
    const preQuizValid = this.quizPreQuestionCount >= minimumQuestions
    const postQuizValid = this.quizPostQuestionCount >= minimumQuestions
    return preQuizValid && postQuizValid
  }

  /**
   * Retourne les messages d'erreur pour les quiz manquants ou incomplets
   */
  getQuizValidationMessages(): string[] {
    const messages: string[] = []

    if (!this.quizPre) {
      messages.push('Quiz de pré-évaluation manquant')
    } else if (this.quizPreQuestionCount < 5) {
      const missing = 5 - this.quizPreQuestionCount
      messages.push(
        `${missing} question${missing > 1 ? 's' : ''} manquante${missing > 1 ? 's' : ''} pour le quiz pré`
      )
    }

    if (!this.quizPost) {
      messages.push('Quiz de post-évaluation manquant')
    } else if (this.quizPostQuestionCount < 5) {
      const missing = 5 - this.quizPostQuestionCount
      messages.push(
        `${missing} question${missing > 1 ? 's' : ''} manquante${missing > 1 ? 's' : ''} pour le quiz post`
      )
    }

    return messages
  }

  get canBePublished(): boolean {
    return (
      this.quizzesAreValid &&
      this.hasEnoughQuestions() &&
      this.learningSupports.length > 0 &&
      this.isDraft
    )
  }

  // Obtenir les supports triés par ordre
  getSortedSupports(): LearningSupport[] {
    return [...this.learningSupports].sort((a, b) => a.order - b.order)
  }

  get isActive(): boolean {
    return this.status === LearningStatus.Values.ACTIVE
  }

  get isDraft(): boolean {
    return this.status === LearningStatus.Values.DRAFT
  }

  get postQuizVersionNumber(): number | null {
    return this.quizPost?.activeVersion?.versionNumber ?? null
  }

  addNewSupport() {
    const newSupport = LearningSupport.createEmpty()
    newSupport.order = this.learningSupports.length + 1
    this.learningSupports.push(newSupport)

    return newSupport
  }

  hasInvalidSupports(): boolean {
    return this.learningSupports.some((s) => !s.isValid())
  }

  // Ce que l'édition du grain peut modifier, copié pour pouvoir l'annuler
  captureEditableState(): LearningGrainEditableState {
    return {
      title: this.title,
      points: this.points,
      learningSupports: this.learningSupports.map((support) => support.clone())
    }
  }

  restoreEditableState(state: LearningGrainEditableState) {
    this.title = state.title
    this.points = state.points
    this.learningSupports = state.learningSupports.map((support) => support.clone())
  }

  deleteAllSupportDrafts() {
    this.learningSupports = this.learningSupports.filter((s) => s.id !== 0)
  }

  // Convertir vers le format d'API pour la mise à jour
  toApiFormat(): Record<string, any> {
    return {
      id: this.id,
      title: this.title,
      status: this.status,
      points: this.points,
      order: this.order,
      learningSupports: this.learningSupports.map((support) => support.toApiFormat())
    }
  }

  // Créer un objet de grain pour l'entrée API
  toInputFormat(): LearningGrainInput {
    return {
      id: this.id,
      title: this.title,
      status: this.status,
      points: this.points,
      order: this.order,
      // L'ordre enregistré suit la position à l'écran (1, 2, 3...), sans trou ni doublon
      learningSupports: this.learningSupports.map((support, index) => {
        const ordered = support.clone()
        ordered.order = index + 1
        return ordered
      }),
      postQuizVersionNumber: this.postQuizVersionNumber
    }
  }

  public static createEmpty(): LearningGrain {
    return new LearningGrain({
      id: 0,
      title: 'Nouveau grain',
      status: LearningStatus.Values.DRAFT,
      points: 0,
      order: 0,
      quizPre: null,
      quizPost: null,
      learningSupports: []
    })
  }

  // Créer une instance de LearningModuleGrain à partir de données brutes
  public static create(data: unknown): LearningGrain {
    return EntityValidator.validateAndCreate(
      LearningGrainSchema,
      data,
      LearningGrain,
      'LearningGrain'
    )
  }
}

export interface LearningGrainEditableState {
  title: string
  points: number
  learningSupports: LearningSupport[]
}

/**
 * Représentation de l'objet Input
 */
export class LearningGrainInput {
  id: number
  title: string
  status: LearningStatus.Values
  points: number
  order: number
  learningSupports?: LearningSupport[]
  postQuizVersionNumber: number | null = null

  constructor(grain: LearningGrainInputEntity) {
    this.id = grain.id
    this.title = grain.title
    this.status = grain.status
    this.points = grain.points
    this.order = grain.order
    this.postQuizVersionNumber = grain.postQuizVersionNumber ?? null

    // Conversion des supports
    if (grain.learningSupports)
      this.learningSupports = grain.learningSupports.map((support) =>
        LearningSupport.create(support)
      )
  }
}
